import './PhoneEditModal.scss'
import React, { useState, useEffect, useContext } from 'react'
import PropTypes from 'prop-types'
import { isValidPhoneNumber } from 'react-phone-number-input'
import { I18nReactContext } from '../../../contexts/I18nContext'
import { CompanyReactContext } from '../../../contexts/CompanyContext'
import { Modal } from '../../Smart/Modal/Modal'
import { Button } from '../../Form/Button/Button'
import { buttonColors } from '../../Pickers/InputTheme'
import { InternationalPhoneInput } from '../../Form/InternationalPhoneInput/InternationalPhoneInput'
import { checkPhoneAvailabilityAPI, updateUserPhoneAPI } from '../../../Services/api'
import useDebounce from '../../../Hooks/useDebounce'
import { DEFAULT_COUNTRY_CODE } from '../../../Utils/Constants'
import { formatPhoneToE164 } from '../../../Utils/functions'

/**
 * Modal for editing a user's phone number with real-time availability validation
 *
 * Features:
 * - Pre-fills with current phone number
 * - Real-time phone format validation
 * - Debounced availability check against other users in the company
 * - Inline error display for validation and availability issues
 * - Uses WeezBee Modal pattern for consistent styling
 *
 * @param {object} props - Component props
 * @param {string} props.userId - User ID being edited
 * @param {string} props.currentPhone - Current phone number in E.164 format
 * @param {string} props.currentCountryCode - Current ISO 2-letter country code
 * @param {boolean} props.isOpen - Whether modal is open
 * @param {function} props.onClose - Close handler
 * @param {function} props.onSuccess - Success callback with {phone, phoneCountryCode}
 * @param {function} [props.checkAvailabilityApi] - Availability-check API ({userId, phone, phoneCountryCode}); defaults to the admin endpoint. Pass the SA variant for SuperAdmin use.
 * @param {function} [props.updatePhoneApi] - Phone-update API ({userId, phone, phoneCountryCode}); defaults to the admin endpoint. Pass the SA variant for SuperAdmin use.
 */
export const PhoneEditModal = ({
    userId,
    currentPhone,
    currentCountryCode,
    isOpen,
    onClose,
    onSuccess,
    checkAvailabilityApi = checkPhoneAvailabilityAPI,
    updatePhoneApi = updateUserPhoneAPI
}) => {
    const { i18n } = useContext(I18nReactContext)
    const companyContext = useContext(CompanyReactContext)

    // Phone state
    const [phone, setPhone] = useState('')
    const [phoneCountryCode, setPhoneCountryCode] = useState('')
    const [originalPhoneE164, setOriginalPhoneE164] = useState('') // Original phone in E.164 for comparison

    // Loading states
    const [isLoading, setIsLoading] = useState(false)
    const [isChecking, setIsChecking] = useState(false)


    // Availability state
    const [availabilityStatus, setAvailabilityStatus] = useState(null) // 'available' | 'unavailable' | null
    const [availabilityReason, setAvailabilityReason] = useState(null) // Specific error from API

    // Debounced phone value for availability check (400ms delay)
    const debouncedPhone = useDebounce({ value: phone, delay: 400 })

    // Initialize state on modal open
    useEffect(() => {
        if (isOpen) {
            // Get effective country code with fallback hierarchy:
            // 1. Current user's country code (from props)
            // 2. Company's country code (from context)
            // 3. Default country code (IL)
            const effectiveCountryCode = currentCountryCode || companyContext?.countryCode || DEFAULT_COUNTRY_CODE
            // Convert phone to E.164 format for proper display with country flag
            // This handles cases where phone is stored as local format (e.g., '0502197932')
            const phoneE164 = formatPhoneToE164(currentPhone, effectiveCountryCode)
            setPhone(phoneE164 || '')
            setOriginalPhoneE164(phoneE164 || '') // Store original for comparison
            setPhoneCountryCode(effectiveCountryCode)
            setAvailabilityStatus(null)
            setAvailabilityReason(null)
            setIsChecking(false)
            setIsLoading(false)
        }
    }, [isOpen, currentPhone, currentCountryCode, companyContext?.countryCode])

    /**
     * Unified phone validation with hierarchical checks
     * Returns validation status used by both availability check trigger and save button
     *
     * Validation Hierarchy:
     * 1. Phone unchanged → can't check, can't save (nothing to do - most fundamental)
     * 2. Empty phone → can't check, can't save
     * 3. Phone too short → can't check, can't save
     * 4. Invalid format → can't check, can't save
     * 5. Valid format → check availability → result determines save
     */
    const getPhoneValidationStatus = () => {
        // Level 1: Unchanged from original (most fundamental - nothing to do)
        // Compare against originalPhoneE164 since phone is now in E.164 format
        if (phone === originalPhoneE164) {
            return {
                canCheck: false,
                canSave: false,
                reason: 'unchanged',
                showError: false
            }
        }

        // Level 2: Empty check
        if (!phone) {
            return {
                canCheck: false,
                canSave: false,
                reason: null,
                showError: false
            }
        }


        // Level 3: Invalid phone format
        if (!isValidPhoneNumber(phone)) {
            return {
                canCheck: false,
                canSave: false,
                reason: 'invalid_format',
                showError: true
            }
        }

        // Level 4: Valid format - availability determines save
        return {
            canCheck: true,
            canSave: availabilityStatus === 'available',
            reason: availabilityStatus === 'unavailable' ? 'unavailable' : null,
            showError: availabilityStatus === 'unavailable'
        }
    }

    // Get current validation status
    const validationStatus = getPhoneValidationStatus()

    // Save button uses unified validation
    const isSaveDisabled = isLoading || isChecking || !validationStatus.canSave

    /**
     * Get error message based on validation status
     */
    const getErrorMessage = () => {
        if (!validationStatus.showError || !validationStatus.reason) return null

        switch (validationStatus.reason) {
            case 'invalid_format':
                return i18n.translate('phoneEditModal.errors.invalid_phone_format')
            case 'unavailable':
                return i18n.translate(`phoneEditModal.errors.${availabilityReason || 'phone_already_exists'}`)
            default:
                return null
        }
    }

    /**
     * Handle phone value change from InternationalPhoneInput
     * @param {string} newPhone - Phone number in E.164 format
     */
    const handlePhoneChange = (newPhone) => {
        const phoneValue = newPhone || ''
        setPhone(phoneValue)
        setAvailabilityStatus(null)
        setAvailabilityReason(null)

        // Debounced availability check triggered via useEffect
    }

    /**
     * Handle country code change from InternationalPhoneInput
     * @param {string} newCountry - ISO 2-letter country code
     */
    const handleCountryChange = (newCountry) => {
        setPhoneCountryCode(newCountry)

        // Reset availability since country affects phone validation
        setAvailabilityStatus(null)
        setAvailabilityReason(null)
    }


    /**
     * Triggers availability check when debounced phone value changes and validation passes
     * Handles stale responses when phone changes during check
     */
    useEffect(() => {
        // Check if we should perform availability check
        // Compare against originalPhoneE164 since both are in E.164 format
        const shouldCheck = debouncedPhone &&
            debouncedPhone !== originalPhoneE164 &&
            debouncedPhone.length >= 8 &&
            isValidPhoneNumber(debouncedPhone)

        if (!shouldCheck) return

        const phoneToCheck = debouncedPhone  // Capture current value to detect stale responses
        let isCancelled = false

        const checkAvailability = async () => {
            setIsChecking(true)

            try {
                const result = await checkAvailabilityApi({
                    userId,
                    phone: phoneToCheck,
                    phoneCountryCode
                })

                // Ignore stale response if effect was cleaned up
                if (isCancelled) return

                if (result.available) {
                    setAvailabilityStatus('available')
                    setAvailabilityReason(null)
                } else {
                    setAvailabilityStatus('unavailable')
                    setAvailabilityReason(result.reason) // e.g., 'phone_already_exists'
                }
            } catch (err) {
                // Ignore stale error if effect was cleaned up
                if (isCancelled) return
                setAvailabilityStatus('unavailable')
                setAvailabilityReason('check_failed')
            } finally {
                if (!isCancelled) {
                    setIsChecking(false)
                }
            }
        }

        checkAvailability()

        // Cleanup function to handle stale responses
        return () => {
            isCancelled = true
        }
    }, [debouncedPhone, originalPhoneE164, phoneCountryCode, userId])

    /**
     * Handle save button click - updates user phone via API
     */
    const handleSave = async () => {
        // Double-check validation before save (defensive)
        if (!validationStatus.canSave) return

        setIsLoading(true)
        try {
            await updatePhoneApi({ userId, phone, phoneCountryCode })
            onSuccess({ phone, phoneCountryCode })
            onClose()
        } catch (err) {
            // Handle specific error codes from save API
            const errorKey = err?.response?.data?.error || 'network_error'
            setAvailabilityStatus('unavailable')
            setAvailabilityReason(errorKey)
        } finally {
            setIsLoading(false)
        }
    }

    const errorMessage = getErrorMessage()

    return (
        <Modal
            open={isOpen}
            onClose={onClose}
        >
            <div className="PhoneEditModal modalWrapper" data-testid="phone-edit-modal">
                <div className="modalTitle" data-testid="phone-edit-modal-title">
                    <div>{i18n.translate('phoneEditModal.title')}</div>
                </div>
                <div className="modalContent">
                    <div className="PhoneEditModal__phone-wrapper" aria-busy={isChecking}>
                        <InternationalPhoneInput
                            value={phone}
                            onChange={handlePhoneChange}
                            defaultCountry={phoneCountryCode}
                            onCountryChange={handleCountryChange}
                            label={i18n.translate('phoneEditModal.phoneLabel')}
                            placeholder={i18n.translate('phoneEditModal.phonePlaceholder')}
                            error={errorMessage}
                            disabled={isLoading}
                            dataTestId="phone-edit-modal-phone-input"
                        />
                        {errorMessage && (
                            <div
                                role="alert"
                                aria-live="polite"
                                className="PhoneEditModal__error-announcement"
                                data-testid="phone-edit-modal-error"
                            >
                                {errorMessage}
                            </div>
                        )}
                    </div>
                </div>
                <div className="modalFooter">
                    <Button
                        color={buttonColors.YELLOW}
                        onClick={onClose}
                        disabled={isLoading}
                        text={i18n.translate('phoneEditModal.cancel')}
                        dataTestId="phone-edit-modal-cancel"
                    />
                    <Button
                        color={buttonColors.GREY}
                        onClick={handleSave}
                        disabled={isSaveDisabled}
                        loading={isLoading}
                        text={i18n.translate('phoneEditModal.save')}
                        dataTestId="phone-edit-modal-save"
                    />
                </div>
            </div>
        </Modal>
    )
}

PhoneEditModal.propTypes = {
    userId: PropTypes.string.isRequired,
    currentPhone: PropTypes.string.isRequired,
    currentCountryCode: PropTypes.string,
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSuccess: PropTypes.func.isRequired,
    checkAvailabilityApi: PropTypes.func,
    updatePhoneApi: PropTypes.func
}
